"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger, NavigationMenuContent, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        <div className="h-5 w-16 bg-muted animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-4">
        {menuItems.filter(item => (item.label === "Home" || item.label === "Contact") && item.label !== "Schedule Now").map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              {item.children && item.children.length > 0 ? <>
                  <NavigationMenuTrigger className="bg-transparent p-0 h-auto text-sm font-bold uppercase text-foreground transition-colors duration-[40ms] hover:text-primary data-[state=open]:text-primary focus:bg-transparent">
                    {item.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="min-w-[200px] bg-background border border-border shadow-lg rounded-md p-2">
                    <ul className="flex flex-col gap-1">
                      {item.children.map((child, childIdx) => <li key={child.id}>
                          <NavigationMenuLink asChild>
                            <Link to={child.href || "#"} className="block px-4 py-2 text-sm font-medium text-foreground hover:bg-accent hover:text-primary rounded-sm transition-colors">
                              {child.label}
                            </Link>
                          </NavigationMenuLink>
                        </li>)}
                    </ul>
                  </NavigationMenuContent>
                </> : <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="text-sm font-bold uppercase text-foreground transition-colors duration-[40ms] hover:text-primary">
                    {item.label}
                  </Link>
                </NavigationMenuLink>}
            </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const toggleExpand = (id: string) => {
    setExpandedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  return <nav className="flex flex-col py-6 gap-4">
      {loading ? <div className="flex flex-col gap-4">
          <div className="h-10 w-full bg-muted animate-pulse rounded" />
          <div className="h-10 w-full bg-muted animate-pulse rounded" />
          <div className="h-10 w-full bg-muted animate-pulse rounded" />
        </div> : menuItems.filter(item => (item.label === "Home" || item.label === "Contact") && item.label !== "Schedule Now").map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      const isExpanded = expandedItems.includes(item.id);
      return <div key={item.id} data-index={i} className="flex flex-col">
                {hasChildren ? <>
                    <Button variant="ghost" onClick={() => toggleExpand(item.id)} className="flex items-center justify-between w-full py-2 h-auto text-base font-bold uppercase text-foreground transition-colors hover:text-primary hover:bg-transparent px-0 text-left">
                      <span>{item.label}</span>
                      <ChevronDown className={cn("h-5 w-5 transition-transform duration-200", isExpanded && "rotate-180")} />
                    </Button>
                    {isExpanded && <div className="flex flex-col pl-4 mt-1 border-l-2 border-primary/20 gap-1">
                        {item.children?.map(child => <Link key={child.id} to={child.href || "#"} className="block py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
                            {child.label}
                          </Link>)}
                      </div>}
                  </> : <Link to={item.href || "#"} className="block py-2 text-base font-bold uppercase text-foreground transition-colors hover:text-primary">
                    {item.label}
                  </Link>}
              </div>;
    })}
      <div className="pt-4 border-t border-border">
        <Button className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-wide shadow-md">
          Schedule Now
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex-shrink-0 flex items-center">
            <Link to="/">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/trusted_inspections_logo_primary.png" alt="Trusted Inspections" className="h-7" />
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <MenuProvider menu_id="16">
              <DesktopNav />
            </MenuProvider>
            
            <div className="hidden md:block">
              <Button className="bg-primary text-primary-foreground font-bold uppercase tracking-wide shadow-md transition-transform hover:-translate-y-0.5 hover:shadow-lg">
                Schedule Now
              </Button>
            </div>

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary" aria-label="Toggle menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader className="text-left">
                  <SheetTitle className="text-primary font-bold uppercase tracking-tight">Navigation</SheetTitle>
                  <SheetDescription className="sr-only">
                    Mobile navigation menu for Trusted Inspections
                  </SheetDescription>
                </SheetHeader>
                <MenuProvider menu_id="16">
                  <MobileMenuConsumer />
                </MenuProvider>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>;
}